/**
 * #1535 — Notification Center mutation/query hooks.
 *
 * Two consumers share these hooks:
 *   - Header.tsx bell dropdown — the "real-time quick-glance" surface.
 *     Reads `useNotifications({limit:10})` + `useUnreadNotificationCount`,
 *     mutates via `useMarkNotificationRead` + `useMarkAllNotificationsRead`.
 *   - NotificationCenterPage.tsx — the dedicated /notifications page.
 *     The grid pulls rows via the SSRM datasource (POST /notifications/ssrm);
 *     row-level / bulk / single mutations land through the new
 *     `useDeleteNotification`, `useToggleNotificationRead`, and
 *     `useBulkNotificationAction` hooks. All four invalidate the
 *     `['notifications']` query key so the bell counter / dropdown reflect
 *     the change in real time.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { Notification } from '@tracxtms/shared';
import { useDeferredReady } from './use-deferred-ready';

interface NotificationFilters {
  page?: number;
  limit?: number;
  unreadOnly?: boolean;
  module?: string[];
  category?: string[];
  severity?: string[];
  readState?: 'all' | 'read' | 'unread';
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  includeDeleted?: boolean;
}

function filtersToParams(filters: NotificationFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.page !== undefined) params.set('page', String(filters.page));
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.unreadOnly) params.set('unreadOnly', 'true');
  if (filters.module && filters.module.length > 0) params.set('module', filters.module.join(','));
  if (filters.category && filters.category.length > 0) params.set('category', filters.category.join(','));
  if (filters.severity && filters.severity.length > 0) params.set('severity', filters.severity.join(','));
  if (filters.readState && filters.readState !== 'all') params.set('readState', filters.readState);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.search) params.set('search', filters.search);
  if (filters.includeDeleted) params.set('includeDeleted', 'true');
  return params;
}

export function useNotifications(filters: NotificationFilters = {}) {
  const { page = 1, limit = 20, unreadOnly = false } = filters;
  // #1429: non-critical shell data — defer off the cold-load critical path.
  const deferredReady = useDeferredReady();
  return useQuery({
    queryKey: ['notifications', { page, limit, unreadOnly, ...filters }],
    queryFn: async () => {
      const params = filtersToParams({ ...filters, page, limit, unreadOnly });
      const res = await api.get<{ data: Notification[]; pagination: { total: number } }>(
        `/notifications?${params}`
      );
      return res.data;
    },
    enabled: deferredReady,
    refetchInterval: 30000, // Poll every 30s
  });
}

export function useUnreadNotificationCount() {
  // #1429: non-critical shell data — defer off the cold-load critical path.
  const deferredReady = useDeferredReady();
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const res = await api.get<{ data: { count: number } }>('/notifications/unread-count');
      return res.data.data.count;
    },
    enabled: deferredReady,
    refetchInterval: 15000, // Poll every 15s
  });
}

// Legacy hook — kept for bell dropdown. New page uses
// useToggleNotificationRead so it can also flip back to unread.
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.patch(`/notifications/${id}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    // #1535 — accepts an optional filter set so the new
    // "Mark all as read within current filter" toolbar can scope the call.
    mutationFn: async (filter: Partial<NotificationFilters> = {}) => {
      await api.patch('/notifications/read-all', filter);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// #1535 — Toggle single read state. `{read: true}` marks, `{read: false}` unmarks.
export function useToggleNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, read }: { id: string; read: boolean }) => {
      await api.patch(`/notifications/${id}`, { read });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// #1535 — Soft-delete a notification (sets deletedAt; row hidden from
// list by default).
export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/notifications/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

// #1535 — Bulk read/unread/delete. Returns server response so the page
// can surface "X of Y updated, Z forbidden" toast text.
export function useBulkNotificationAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      ids: string[];
      action: 'read' | 'unread' | 'delete';
    }) => {
      const res = await api.patch<{
        data: { updated: number; forbiddenIds: string[] };
      }>('/notifications/bulk', input);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
